// Entry point for the build script in your package.json
import { Turbo } from '@hotwired/turbo-rails'

// Custom Turbo Stream action: full-page navigation
// Usage: turbo_stream.action(:redirect, url)
Turbo.StreamActions.redirect = function () {
  Turbo.visit(this.getAttribute('target'))
}
import 'flowbite/dist/flowbite.turbo'
import $ from 'jquery'
import 'jquery-modal'

import './channels'
import './color'
import './controllers'
import './notifications/declarative_web_push'
import { reloadOnReturnFromIdle } from './idle_reload'
import './svg_render'
import { isMobile, isPwa } from './utilities'
import { FirebaseNotificationManager } from './notifications/firebase_notifications'

window.jQuery = $
window.$ = $

function completeLoadingOverlayStep (overlay, completedSteps, step) {
  if (completedSteps.has(step.key)) return

  completedSteps.add(step.key)

  const bar = overlay.querySelector('#page-loading-overlay-bar')
  const status = overlay.querySelector('#page-loading-overlay-status')
  const progress = (completedSteps.size / overlay._loadingOverlaySteps.length) * 100

  if (bar) bar.style.width = `${progress}%`
  if (status) {
    status.textContent = `${step.label} (${completedSteps.size}/${overlay._loadingOverlaySteps.length})`
  }

  if (completedSteps.size !== overlay._loadingOverlaySteps.length) return

  window.setTimeout(() => {
    overlay.classList.add('is-complete')
    document.body.classList.remove('loading-overlay-active')
    document.documentElement.classList.remove('show-initial-loading-overlay')
  }, 120)
}

function closeTimezoneSyncDialog (dialog) {
  dialog.classList.add('hidden')
}

function finalizeLoadingOverlay (overlay) {
  if (overlay.dataset.loadingOverlayFinalized === 'true') return

  overlay.dataset.loadingOverlayFinalized = 'true'
  overlay.remove()
  initializeTimezoneSyncDialog()
}

function createLoadingOverlayMarker (index, stepCount) {
  const marker = document.createElement('span')

  marker.className = 'page-loading-overlay__marker'
  marker.style.left = `${(index / stepCount) * 100}%`

  return marker
}

function createLoadingOverlayStep (key, label, wait) {
  return { key, label, wait }
}

function initializeLoadingOverlay () {
  const overlay = document.querySelector('[data-loading-overlay]')

  if (!overlay ||
    overlay.dataset.loadingOverlayInitialized === 'true' ||
    !document.documentElement.classList.contains('show-initial-loading-overlay')) {
    return
  }

  overlay.dataset.loadingOverlayInitialized = 'true'
  document.body.classList.add('loading-overlay-active')

  const completedSteps = new Set()
  const markers = overlay.querySelector('#page-loading-overlay-markers')
  const steps = [
    createLoadingOverlayStep('dom', 'Elements loaded', waitForLoadingOverlayDom()),
    createLoadingOverlayStep('css', 'CSS loaded', waitForLoadingOverlayStylesheets()),
    createLoadingOverlayStep('assets', 'Assets loaded', waitForLoadingOverlayWindow()),
    createLoadingOverlayStep('paint', 'Paint locked in', waitForLoadingOverlayPaint())
  ]

  if (document.fonts?.ready) {
    steps.splice(2, 0, createLoadingOverlayStep('fonts', 'Fonts loaded', document.fonts.ready))
  }

  overlay._loadingOverlaySteps = steps

  if (markers) {
    markers.replaceChildren()

    steps.slice(1).forEach((step, index) => {
      markers.append(createLoadingOverlayMarker(index + 1, steps.length))
    })
  }

  steps.forEach(step => {
    Promise.resolve(step.wait)
      .catch(() => {})
      .then(() => completeLoadingOverlayStep(overlay, completedSteps, step))
  })

  overlay.addEventListener('transitionend', function handleTransitionEnd (event) {
    if (event.target !== overlay || event.propertyName !== 'opacity') return

    overlay.removeEventListener('transitionend', handleTransitionEnd)
    finalizeLoadingOverlay(overlay)
  })

  window.setTimeout(() => finalizeLoadingOverlay(overlay), 600)
}

function initializeTimezoneSyncDialog () {
  const dialog = document.querySelector('[data-timezone-sync-dialog]')

  if (!dialog || dialog.dataset.timezoneSyncInitialized === 'true') return

  dialog.dataset.timezoneSyncInitialized = 'true'

  const frontendTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const backendTimezone = dialog.dataset.timezoneSyncBackendTimezone
  const backendTimezoneTz = dialog.dataset.timezoneSyncBackendTz

  if (!frontendTimezone || !backendTimezoneTz || frontendTimezone === backendTimezoneTz) return

  const backendLabel = dialog.querySelector('[data-timezone-sync-backend-label]')
  const confirmButton = dialog.querySelector('[data-timezone-sync-confirm]')
  const dismissButton = dialog.querySelector('[data-timezone-sync-dismiss]')
  const error = dialog.querySelector('[data-timezone-sync-error]')
  const frontendLabel = dialog.querySelector('[data-timezone-sync-frontend-label]')

  if (backendLabel) backendLabel.textContent = backendTimezone
  if (frontendLabel) frontendLabel.textContent = frontendTimezone

  const dismiss = () => closeTimezoneSyncDialog(dialog)

  dismissButton?.addEventListener('click', dismiss, { once: true })

  confirmButton?.addEventListener('click', async () => {
    if (!confirmButton.dataset.defaultLabel) {
      confirmButton.dataset.defaultLabel = confirmButton.textContent
    }

    confirmButton.disabled = true
    confirmButton.textContent = 'Updating...'
    error?.classList.add('hidden')

    try {
      const response = await fetch(dialog.dataset.timezoneSyncUpdateUrl, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ user: { browser_timezone: frontendTimezone } })
      })

      if (!response.ok) throw new Error('Timezone update failed')

      closeTimezoneSyncDialog(dialog)
    } catch (updateError) {
      error?.classList.remove('hidden')
      confirmButton.disabled = false
      confirmButton.textContent = confirmButton.dataset.defaultLabel || 'Update timezone'
    }
  })

  dialog.classList.remove('hidden')
}

function waitForLoadingOverlayDom () {
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    return Promise.resolve()
  }

  return new Promise(resolve => {
    document.addEventListener('DOMContentLoaded', resolve, { once: true })
  })
}

function waitForLoadingOverlayPaint () {
  return waitForLoadingOverlayWindow().then(() => new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve)
    })
  }))
}

function waitForLoadingOverlayStylesheets () {
  const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))

  if (stylesheets.length === 0) return Promise.resolve()

  return Promise.all(stylesheets.map(stylesheet => new Promise(resolve => {
    if (stylesheet.sheet) {
      resolve()
      return
    }

    const done = () => {
      stylesheet.removeEventListener('error', done)
      stylesheet.removeEventListener('load', done)
      resolve()
    }

    stylesheet.addEventListener('load', done, { once: true })
    stylesheet.addEventListener('error', done, { once: true })
  })))
}

function waitForLoadingOverlayWindow () {
  if (document.readyState === 'complete') return Promise.resolve()

  return new Promise(resolve => {
    window.addEventListener('load', resolve, { once: true })
  })
}

// Custom click events
document.addEventListener('click', (event) => {
  // Fade in button
  if (event.target.matches('[data-fade-in-id]')) {
    $(event.target).hide()
    $('#' + $(event.target).data('fade-in-id')).fadeIn(250)
  }

  // Toggle button
  const showHideTarget = event.target.closest('[data-show-hide-id]')
  if (showHideTarget) {
    const elem = $('#' + $(showHideTarget).data('show-hide-id'))
    elem.toggleClass('hidden')
  }

  // Fade Toggle button
  const fadeShowHideTarget = event.target.closest('[data-fade-show-hide-id]')
  if (fadeShowHideTarget) {
    const elem = $('#' + $(fadeShowHideTarget).data('fade-show-hide-id'))
    elem.stop(true, true)
    if (fadeShowHideTarget.hasAttribute('aria-expanded')) {
      const expanded = elem.css('display') === 'none'
      fadeShowHideTarget.setAttribute('aria-expanded', String(expanded))
      elem.attr('aria-hidden', String(!expanded)).prop('inert', !expanded)
    }
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250
    elem.fadeToggle(duration)
  }

  // Slide in button
  const slideInTarget = event.target.closest('[data-slide-in-id]')
  if (slideInTarget) {
    const elem = $('#' + $(slideInTarget).data('slide-in-id'))
    elem.slideToggle(250)
  }

  // Rotatable buttons
  if (event.target.matches('[data-rotate]')) {
    const button = $(event.target)
    let target = button
    if ($(event.target).data('rotate-id') !== undefined) {
      target = $(`#${$(event.target).data('rotate-id')}`)
    }

    switch ($(button).data('rotate')) {
      case 'ccw':
        if ($(target).data('rotated')) {
          $(target).css({ transform: 'rotate(0deg)', transition: 'transform 250ms' })
          $(target).data('rotated', false)
        } else {
          $(target).css({ transform: 'rotate(-90deg)', transition: 'transform 250ms' })
          $(target).data('rotated', true)
        }
        break
      case 'cw':
        if ($(target).data('rotated')) {
          $(target).css({ transform: 'rotate(0deg)', transition: 'transform 250ms' })
          $(target).data('rotated', false)
        } else {
          $(target).css({ transform: 'rotate(90deg)', transition: 'transform 250ms' })
          $(target).data('rotated', true)
        }
        break
    }
  }

  // Hidable button
  if (event.target.matches('[data-hide-on-click]')) {
    $(event.target).toggleClass('hidden')
  }

  // Copy to clipboard
  if (event.target.matches('[data-copy-url]')) {
    const target = $(event.target).data('copy-url')
    navigator.clipboard.writeText(target)
      .then(() => {
        $(event.target).text('Copied to the clipboard!')
      })
      .catch((error) => {
        console.error('Failed to copy to the clipboard:', error)
      })
  }
})

// Idle reload
reloadOnReturnFromIdle()

initializeLoadingOverlay()

// Turbo fades
window.addEventListener('turbo:before-frame-render', (event) => {
  if (document.startViewTransition) {
    const originalRender = event.detail.render
    event.detail.render = (currentElement, newElement) => {
      document.startViewTransition(() => originalRender(currentElement, newElement))
    }
  }
})

window.addEventListener('turbo:before-stream-render', (event) => {
  // Scroll to top if target is home_screen
  if ($(event.srcElement).attr('target') === 'home_screen') {
    window.scrollTo({ top: 0 })
  }

  // Add Turbo Fade
  if (document.startViewTransition) {
    const originalRender = event.detail.render
    event.detail.render = (currentElement, newElement) => {
      document.startViewTransition(() => originalRender(currentElement, newElement))
    }
  }

  // Add hide on max children
  const targetString = $(event.srcElement).attr('target')
  const targetElem = $(`#${targetString}`)
  const elemToCheck = [targetElem, targetElem.parent()]

  const turboAction = $(event.srcElement).attr('action')
  let elemDelta = 0
  if (turboAction.includes('append')) {
    elemDelta++
  } else if (turboAction.includes('remove')) {
    elemDelta--
  }

  elemToCheck.forEach((elem) => {
    if (elem.data('max-children') !== undefined) {
      const max = elem.data('max-children')
      const newCount = elem.children().length + elemDelta
      const hideId = elem.data('hide-id')
      const hideElem = $(`#${hideId}`)

      if (newCount >= max) {
        hideElem.addClass('hidden')
      } else {
        hideElem.removeClass('hidden')
      }
    }
  })
})

// Resize text areas
function resizeTextarea (textarea) {
  const mirror = textareaMirror(textarea)
  const width = textarea.getBoundingClientRect().width + 'px'

  if (mirror.style.width !== width) mirror.style.width = width
  mirror.value = textarea.value

  const requiredHeight = Math.max(mirror.offsetHeight, mirror.scrollHeight)

  if (Math.abs(requiredHeight - textarea.offsetHeight) <= 1) return

  textarea.style.height = requiredHeight + 'px'
}

function queueTextareaResize (textarea) {
  if (textarea._autosizeFrame) {
    cancelAnimationFrame(textarea._autosizeFrame)
  }

  textarea._autosizeFrame = requestAnimationFrame(() => {
    resizeTextarea(textarea)
    textarea._autosizeFrame = null
  })
}

function applyAutosize () {
  removeOrphanedTextareaMirrors()

  const textareas = document.querySelectorAll('textarea.auto-resize')

  textareas.forEach(textarea => {
    if (!textarea._autosizeHandler) {
      textarea._autosizeHandler = () => queueTextareaResize(textarea)
      textarea.addEventListener('input', textarea._autosizeHandler)
    }

    // Only resize immediately when the textarea already has content (e.g. edit
    // forms). Empty textareas keep their rows-attribute height so they are not
    // inflated by a zero-width container during a CSS width transition.
    if (textarea.value.trim() !== '') {
      queueTextareaResize(textarea)
    }
  })
}

function removeOrphanedTextareaMirrors () {
  document.querySelectorAll('[data-autosize-mirror]').forEach(mirror => {
    if (!mirror._autosizeSource?.isConnected) mirror.remove()
  })
}

function textareaMirror (textarea) {
  if (textarea._autosizeMirror?.isConnected) return textarea._autosizeMirror

  // Measuring a separate element avoids collapsing a focused iOS textarea on every keystroke.
  const mirror = textarea.cloneNode()
  const discardedAttributes = ['autofocus', 'id', 'name', 'required']

  Array.from(mirror.attributes).forEach(attribute => {
    if (attribute.name.startsWith('data-') || discardedAttributes.includes(attribute.name)) {
      mirror.removeAttribute(attribute.name)
    }
  })

  mirror.classList.remove('auto-resize')
  mirror.dataset.autosizeMirror = 'true'
  mirror.setAttribute('aria-hidden', 'true')
  mirror.tabIndex = -1
  Object.assign(mirror.style, {
    height: 'auto',
    left: '0',
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'fixed',
    resize: 'none',
    top: '0',
    visibility: 'hidden',
    zIndex: '-1'
  })

  mirror._autosizeSource = textarea
  textarea._autosizeMirror = mirror
  document.body.append(mirror)

  return mirror
}

function clearThankfulnessForm (form) {
  const input = form.querySelector('[data-thankfulness-input="true"]')
  if (!input) return

  input.value = ''
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.focus()
}

function submitThankfulnessOnEnter (event) {
  if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
    return
  }

  const input = event.target.closest('[data-thankfulness-input="true"]')
  if (!input) return

  event.preventDefault()
  input.form.requestSubmit()
}

document.addEventListener('turbo:load', () => {
  applyAutosize()
})

// Update textareas after Turbo Stream replace actions
const afterRenderEvent = new Event('turbo:after-stream-render')
document.addEventListener('turbo:before-stream-render', (event) => {
  const originalRender = event.detail.render

  event.detail.render = function (streamElement) {
    originalRender(streamElement)
    document.dispatchEvent(afterRenderEvent)
  }
})

document.addEventListener('turbo:after-stream-render', (event) => {
  applyAutosize()
  scrollPrayerUpdatesToBottom()
})

document.addEventListener('keydown', (event) => {
  submitThankfulnessOnEnter(event)
})

document.addEventListener('turbo:submit-end', (event) => {
  if (!event.detail.success) return

  const form = event.target.closest('[data-thankfulness-form="true"]')
  if (!form) return

  clearThankfulnessForm(form)
})

// Scroll prayer request updates to bottom when needed
function scrollPrayerUpdatesToBottom () {
  const containers = document.querySelectorAll('[data-scroll-to-bottom="true"]')
  containers.forEach(container => {
    container.scrollTop = container.scrollHeight
  })
  updateScrollGradients()
}

// Show/hide gradient based on whether content is scrollable
function updateScrollGradients () {
  const scrollContainers = document.querySelectorAll('.updates-scroll-container')
  scrollContainers.forEach(wrapper => {
    const content = wrapper.querySelector('.updates-scroll-content')
    const gradient = wrapper.querySelector('.updates-scroll-gradient')

    if (content && gradient) {
      // Check if content height exceeds container height (is scrollable)
      if (content.scrollHeight > content.clientHeight) {
        gradient.classList.remove('hidden')
      } else {
        gradient.classList.add('hidden')
      }
    }
  })
}

document.addEventListener('turbo:load', () => {
  scrollPrayerUpdatesToBottom()
  updateScrollGradients()
})

document.addEventListener('turbo:frame-load', () => {
  applyAutosize()
  scrollPrayerUpdatesToBottom()
  updateScrollGradients()
})

document.addEventListener('DOMContentLoaded', function () {
  // Scroll to bottom of dj activity feeds by default
  const div = document.getElementById('journey_activity_feed')
  if (div) {
    div.scrollTop = div.scrollHeight
  }

  // Initialize Firebase foreground notifications
  initializeFirebaseNotifications()

  // Keep --toolbar-height CSS variable in sync with the actual rendered height
  // of the fixed page toolbar bar. Used by the sidebar to compute its height.
  const toolbarBar = document.getElementById('page-toolbar-bar')
  if (toolbarBar) {
    const updateToolbarHeight = () => {
      document.documentElement.style.setProperty(
        '--toolbar-height',
        toolbarBar.offsetHeight + 'px'
      )
    }
    updateToolbarHeight()
    new ResizeObserver(updateToolbarHeight).observe(toolbarBar)
  }
})

async function initializeFirebaseNotifications () {
  try {
    const supported = FirebaseNotificationManager.isSupported()
    if (supported === true && window.Notification && Notification.permission === 'granted') {
      await FirebaseNotificationManager.initialize()
      console.log('[App] Firebase foreground notifications initialized')
    }
  } catch (err) {
    console.log('[App] Firebase notifications not initialized:', err.message)
  }
}

export function updateFileName (input, id, defaultText = 'Choose File') {
  let fileName = input.files[0]?.name || defaultText
  if (fileName !== defaultText) {
    fileName = fileName.replace(/.*[\/\\]/, '')
    fileName = fileName.length > 20 ? fileName.slice(0, 17) + '...' : fileName
  }
  document.getElementById(id).textContent = fileName
}

window.updateFileName = updateFileName
